import React, { useCallback, useMemo } from "react";
import { ArrayField } from "react-hook-form";
import { useRendersCount } from "react-use";
import useViewer from "../../hooks/useViewer";
import { FormStepProps } from "../../screens/ReviewFormScreen";
import {
  AnalyticsIcon,
  MenuIcon as TextLongIcon,
  SelectorMultipleIcon,
  SelectorSingleIcon,
  StarIcon,
  TextShortIcon,
} from "../icons";
import QuestionFormSlideOver from "./QuestionFormSlideOver";
import QuestionPreview from "./QuestionPreview";

import { QuestionFormInput } from "./QuestionWithOptionsForm";

const QuestionForm: React.FC<
  Omit<FormStepProps, "onNext" | "formState"> & {
    defaultValue: Partial<ArrayField<QuestionFormInput, "id">>;
    index: number;
    onRemove: (index: number) => void;
    focused: boolean;
    onBlur: () => void;
    onFocus: (index: number) => void;
    name: string;
  }
> = React.memo(
  ({
    register,
    unregister,
    setValue,
    getValues,
    trigger,
    reset,
    index,
    control,
    errors,
    defaultValue,
    onRemove,
    focused,
    onBlur,
    onFocus,
    name,
  }) => {
    const icon = useMemo(() => {
      const className = "w-5 h-5 text-primary-500";
      switch (defaultValue.type) {
        case "QuestionText":
          return <TextLongIcon className={className} />;
        case "QuestionSelectorSingle":
          return <SelectorSingleIcon className={className} />;
        case "QuestionSelectorMultiple":
          return <SelectorMultipleIcon className={className} />;
        case "QuestionSkill":
          return <AnalyticsIcon className={className} />;
        case "QuestionEvaluation":
          return <StarIcon className={className} />;
        default:
          return null;
      }
    }, [defaultValue.type]);

    const handleRemove = useCallback(() => onRemove(index), [index, onRemove]);

    const handleFocus = useCallback(() => {
      onFocus(index);
    }, [index]);

    const { companyId } = useViewer();

    return (
      <>
        <div onClick={handleFocus}>
          <QuestionPreview
            control={control}
            defaultValue={defaultValue}
            errors={errors}
            trigger={trigger}
            name={name}
            icon={icon}
            focused={focused}
          />
        </div>
        <input
          type="hidden"
          name={`${name}.type`}
          ref={register()}
          defaultValue={defaultValue.type}
        />
        <input
          type="hidden"
          name={`${name}.companyId`}
          ref={register()}
          defaultValue={companyId}
        />
        <QuestionFormSlideOver
          register={register}
          unregister={unregister}
          setValue={setValue}
          reset={reset}
          getValues={getValues}
          control={control}
          defaultValue={defaultValue}
          onRemove={handleRemove}
          name={name}
          errors={errors}
          open={focused}
          onClose={onBlur}
        />
      </>
    );
  }
);

export default QuestionForm;
